import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, X, HardHat, Package, Wrench, Sparkles, Unlink, AlertTriangle } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { gbp } from "../lib/format";
import { Select, NumberInput } from "./UI";
import { useMaterialUnits } from "../hooks/useMaterialUnits";
import { useMaterialCategories } from "../hooks/useMaterialCategories";
import { MaterialCategoryTree, groupMaterials, treeRows } from "./MaterialCategoryTree";
import {
  entryCostPerUnit, materialsRatePerUnit, resolveEntryWastage, WASTAGE_SOURCE_LABEL,
} from "../lib/recipe";

const newId = () => uuidv4();
const f = (v) => v == null || v === "" ? 0 : Number(v) || 0;

/* LT-28 — unlinked marker.
 * Recipe lines whose reference ID (material_id / labour_rate_id) is null are
 * invisible to the stale-pricing cascade. We display a small muted icon so
 * users can spot lines that will not be tracked when the source cost changes.
 * The line still saves and functions normally — this is purely informational.
 */
function UnlinkedMarker({ kind, testid }) {
  const label =
    kind === "material"
      ? "This material is not linked to the Materials library — cost changes will not track."
      : "This labour rate is not linked to the Labour Rates library — cost changes will not track.";
  return (
    <span
      className="inline-flex items-center text-ink-4 hover:text-ink-3"
      title={label}
      aria-label={label}
      data-testid={testid}
      style={{ marginRight: 4 }}
    >
      <Unlink size={11} />
    </span>
  );
}

/** Site-wide canonical 7 unit options (kept in sync with LibraryEdit). */
export const UNIT_OPTIONS = [
  { value: "m",        label: "m" },
  { value: "linear m", label: "linear m" },
  { value: "m²",       label: "m²" },
  { value: "m³",       label: "m³" },
  { value: "item",     label: "item" },
  { value: "nr",       label: "nr" },
  { value: "N/A",      label: "N/A" },
];

/** Suppress Enter inside text inputs in modal/drawer contexts so the form
 *  never auto-submits. Allows Enter inside textareas for line breaks. */
export const blockEnterSubmit = (e) => {
  if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
    e.preventDefault();
  }
};

/* ============= Material autocomplete (lightweight, flip-up aware) ============= */
// Empty box → the library folded into its categories (click one to open it);
// start typing → flat matches across every category, as before.
// Keyboard: ↑ ↓ move, → opens a category, ← closes it, Enter picks, Esc closes.
export function MaterialAutocomplete({ value, onChange, onSelect, options, placeholder, categories = null }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [hi, setHi] = useState(-1);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const q = (value || "").toLowerCase().trim();
  const treeMode = !q && Array.isArray(categories);
  const catLabel = (key) => (categories || []).find((c) => c.key === key)?.label || key;
  const filtered = useMemo(() => {
    const base = q ? (options || []).filter((o) => `${o.name} (${o.code})`.toLowerCase().includes(q)) : (options || []);
    return base.slice(0, q ? 12 : 8);
  }, [options, q]);
  const groups = useMemo(() => (treeMode ? groupMaterials(options, categories) : []), [treeMode, options, categories]);
  const rows = useMemo(
    () => (treeMode ? treeRows(groups, openKey) : filtered.map((o) => ({ type: "item", material: o }))),
    [treeMode, groups, openKey, filtered],
  );

  useEffect(() => {
    if (!open || !inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    setOpenUp(spaceBelow < 300 && spaceAbove > spaceBelow);
  }, [open, treeMode]);
  // typing pre-highlights the first match so Enter takes it; focusing an
  // already-filled box (or the tree) highlights nothing until an arrow key
  useEffect(() => {
    const el = menuRef.current?.querySelector("[data-hi]");
    if (el) { try { el.scrollIntoView({ block: "nearest" }); } catch (_) {} }
  }, [hi, rows]);

  const pick = (o) => { onSelect(o); setOpen(false); setHi(-1); };
  const toggleCat = (key) => {
    const next = openKey === key ? null : key;
    setOpenKey(next);
    setHi(treeRows(groups, next).findIndex((r) => r.type === "cat" && r.group.key === key));
  };
  const onKeyDown = (e) => {
    const row = rows[hi];
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); if (!open) setOpen(true); else setHi(Math.min(hi + 1, rows.length - 1)); return;
      case "ArrowUp": e.preventDefault(); setHi(Math.max(hi - 1, 0)); return;
      case "Home": if (open && rows.length) { e.preventDefault(); setHi(0); } return;
      case "End": if (open && rows.length) { e.preventDefault(); setHi(rows.length - 1); } return;
      case "ArrowRight":
        if (treeMode && row?.type === "cat" && openKey !== row.group.key) { e.preventDefault(); toggleCat(row.group.key); }
        return;
      case "ArrowLeft":
        if (treeMode && row?.type === "item") { e.preventDefault(); toggleCat(row.group.key); }
        else if (treeMode && row?.type === "cat" && openKey === row.group.key) { e.preventDefault(); toggleCat(row.group.key); }
        return;
      case "Enter":
        e.preventDefault();
        if (!open || !row) return;
        if (row.type === "cat") toggleCat(row.group.key); else pick(row.material);
        return;
      case "Escape": setOpen(false); return;
      default: blockEnterSubmit(e);
    }
  };
  const show = open && rows.length > 0;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        spellCheck="true"
        role="combobox" aria-expanded={show} aria-autocomplete="list"
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(e.target.value.trim() ? 0 : -1); }}
        onFocus={(e) => {
          setOpen(true); setHi(-1);
          requestAnimationFrame(() => { try { e.target.select(); } catch (_) {} });
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={onKeyDown}
        className="w-full bg-transparent border-0 outline-none rounded px-1.5 py-1 focus:bg-bg focus:ring-1 focus:ring-accent/40 text-ink"
        style={{ fontSize: 12.5 }}
        data-testid="material-picker-input"
      />
      {show && (
        <div
          ref={menuRef}
          className={[
            "absolute left-0 right-0 z-50 bg-surface border border-line rounded-md shadow-card overflow-auto",
            treeMode ? "max-h-80" : "max-h-56",
            openUp ? "bottom-full mb-1" : "top-full mt-0.5"
          ].join(" ")}
          data-testid="material-picker-menu"
        >
          {treeMode ? (
            <MaterialCategoryTree rows={rows} openKey={openKey} highlight={hi} onToggle={toggleCat} onSelect={pick} onHover={setHi} />
          ) : filtered.map((o, i) => (
            <button key={o.id} type="button" role="option" aria-selected={i === hi} data-hi={i === hi || undefined}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHi(i)}
              className={["w-full text-left px-3 py-1.5 flex items-baseline gap-2", i === hi ? "bg-accent-soft text-ink" : "text-ink-2 hover:bg-surface-2"].join(" ")}
              style={{ fontSize: 12 }} data-testid={`material-picker-item-${o.id}`}>
              <span className="flex-1 truncate">{o.name} <span className="text-ink-4">({o.code})</span></span>
              {categories && o.category && <span className="text-ink-4 shrink-0" style={{ fontSize: 10.5 }}>{catLabel(o.category)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============= Section shell ============= */
function SectionShell({ title, icon: Icon, accent, total, children, onAdd, addLabel, headerExtra = null }) {
  return (
    <div className="rounded-lg border border-line bg-surface mb-3" data-testid={`recipe-section-${title.toLowerCase().replace(/\s.*/, "")}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-line-soft" style={{ background: accent }}>
        <div className="flex items-center gap-2">
          <Icon size={13} className="text-ink-2" />
          <span className="col-head text-ink-2" style={{ fontWeight: 700 }}>{title}</span>
          {headerExtra}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="col-head" style={{ fontSize: 9 }}>Section total</span>
          <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{gbp(total)}</span>
        </div>
      </div>
      <div className="px-3 py-2">
        {children}
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 h-7 px-3 mt-2 rounded-md bg-accent-soft text-accent hover:bg-accent hover:text-white transition-colors"
            style={{ fontSize: 12, fontWeight: 500 }}
            data-testid={`recipe-add-${title.toLowerCase().replace(/\s.*/, "")}`}
          >
            <Plus size={12} /> {addLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ============= Centred column header helper ============= */
const ColHead = ({ children, className = "" }) => (
  <th
    className={["col-head text-center", className].join(" ")}
    style={{ padding: "4px 6px", textAlign: "center" }}
  >
    {children}
  </th>
);

/* ============= Sections ============= */
export function LabourSection({ entries, onChange, labourRates }) {
  const update = (id, patch) => onChange(entries.map((e) => e.id === id ? { ...e, ...patch } : e));
  const remove = (id) => onChange(entries.filter((e) => e.id !== id));
  const add = () => onChange([...entries, {
    id: newId(), trade_name: "", hours_per_unit: null, hourly_rate: null, labour_rate_id: null
  }]);
  const total = entries.reduce((s, e) => s + f(e.hours_per_unit) * f(e.hourly_rate), 0);

  const tradeOptions = (labourRates || [])
    .filter((lr) => !lr.is_archived)
    .map((lr) => ({
      value: lr.id,
      label: lr.trade_code ? `${lr.trade_name} (${lr.trade_code})` : lr.trade_name,
      rate: lr,
    }));

  return (
    <SectionShell title="Labour" icon={HardHat} accent="#EEF3EE" total={total} onAdd={add} addLabel="Add labour line">
      {entries.length === 0 ? (
        <p className="text-ink-3 px-1 py-2" style={{ fontSize: 11.5 }}>
          No labour entries yet. Add one to break down the per-unit labour cost.
        </p>
      ) : (
        <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="col-head text-left" style={{ width: "44%", padding: "4px 6px" }}>Trade</th>
              <ColHead>Hours / unit</ColHead>
              <ColHead>£/hr</ColHead>
              <ColHead>Per-unit cost</ColHead>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-line-soft">
                <td className="px-1.5 py-1.5">
                  <div className="flex items-center">
                    {e.trade_name && !e.labour_rate_id && (
                      <UnlinkedMarker kind="labour" testid={`recipe-labour-unlinked-${e.id}`} />
                    )}
                    <div className="flex-1">
                      <Select
                        value={e.labour_rate_id || ""}
                        onChange={(id) => {
                          const lr = (labourRates || []).find((x) => x.id === id);
                          if (!lr) return;
                          update(e.id, {
                            labour_rate_id: lr.id,
                            trade_name: lr.trade_name,
                            hourly_rate: lr.hourly_rate ?? null,
                          });
                        }}
                        options={tradeOptions}
                        placeholder={e.trade_name || "Pick a trade…"}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-1 py-1.5" style={{ width: 100 }}>
                  <NumberInput
                    value={e.hours_per_unit}
                    step="0.05"
                    onChange={(v) => update(e.id, { hours_per_unit: v })}
                    suffix="h"
                  />
                </td>
                <td className="px-1 py-1.5" style={{ width: 100 }}>
                  <NumberInput
                    value={e.hourly_rate}
                    step="0.5"
                    onChange={(v) => update(e.id, { hourly_rate: v })}
                    prefix="£"
                  />
                </td>
                <td className="px-2 py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 500, width: 110 }}>
                  {gbp(f(e.hours_per_unit) * f(e.hourly_rate))}
                </td>
                <td className="px-1 py-1.5">
                  <button type="button" onClick={() => remove(e.id)} className="text-ink-4 hover:text-note-red"><X size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionShell>
  );
}

/* LT-20 — per-entry quantity inputs (wastage tier + coverage basis).
 * Blank wastage INHERITS (placeholder shows the inherited value, text names
 * the tier); a typed value is a recipe-tier override with distinct styling
 * and a one-click clear. `lineWastageOverride` (tender drawer only) sits
 * above the recipe tier and is shown read-only here. */
function MaterialQuantityRow({ entry, onPatch, lineWastageOverride, unitLabelFor }) {
  const resolved = resolveEntryWastage(entry, lineWastageOverride);
  const inherited = resolveEntryWastage({ ...entry, wastage_pct: null }, null);
  const isRecipeOverride = entry.wastage_pct !== null && entry.wastage_pct !== undefined && entry.wastage_pct !== "";
  const basis = entry.coverage_basis === "coverage_unit" ? "coverage_unit" : "purchase_unit";
  const cov = Number(entry.coverage_per_unit_applied);
  const hasCoverage = Number.isFinite(cov) && cov > 0;
  const purchaseLabel = unitLabelFor(entry.unit_key_applied) || entry.unit || "unit";
  const coverageLabel = unitLabelFor(entry.coverage_unit_applied) || "coverage unit";
  const coverageError = basis === "coverage_unit" && !hasCoverage;
  const segBtn = (value, label, disabled, testid) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPatch({ coverage_basis: value })}
      data-testid={testid}
      title={disabled ? "This material has no coverage set in the Materials library" : undefined}
      className={[
        "px-2 h-6 rounded-md border transition-colors",
        basis === value ? "bg-ink text-white border-ink" : "bg-surface text-ink-2 border-line hover:bg-surface-2",
        disabled ? "opacity-40 cursor-not-allowed" : "",
      ].join(" ")}
      style={{ fontSize: 10.5, fontWeight: 500 }}
    >
      {label}
    </button>
  );
  return (
    <tr className="bg-surface-2/40" data-testid={`recipe-material-qty-row-${entry.id}`}>
      <td colSpan={6} className="px-2 pb-2 pt-0.5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" style={{ fontSize: 11 }}>
          <div className="flex items-center gap-1.5">
            <span className="text-ink-3">Wastage</span>
            <div style={{ width: 84 }} className={isRecipeOverride ? "rounded-md ring-2 ring-warning/50" : ""}>
              <NumberInput
                value={isRecipeOverride ? entry.wastage_pct : null}
                step="0.5"
                suffix="%"
                placeholder={String(inherited.pct)}
                onChange={(v) => onPatch({ wastage_pct: v })}
                data-testid={`recipe-material-wastage-${entry.id}`}
              />
            </div>
            <span
              className={isRecipeOverride ? "text-warning" : "text-ink-3"}
              data-testid={`recipe-material-wastage-source-${entry.id}`}
              style={{ fontWeight: isRecipeOverride ? 600 : 400 }}
            >
              {lineWastageOverride != null && lineWastageOverride !== ""
                ? `${resolved.pct}% ${WASTAGE_SOURCE_LABEL.line} (recipe value ${isRecipeOverride ? entry.wastage_pct : inherited.pct}% not used)`
                : isRecipeOverride
                  ? WASTAGE_SOURCE_LABEL.recipe
                  : `${inherited.pct}% ${WASTAGE_SOURCE_LABEL[inherited.source]}`}
            </span>
            {isRecipeOverride && (
              <button
                type="button"
                onClick={() => onPatch({ wastage_pct: null })}
                data-testid={`recipe-material-wastage-clear-${entry.id}`}
                className="text-accent hover:underline"
                style={{ fontSize: 10.5 }}
              >
                Clear — inherit
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-ink-3">Qty is in</span>
            {segBtn("purchase_unit", purchaseLabel, false, `recipe-material-basis-purchase-${entry.id}`)}
            {segBtn("coverage_unit", hasCoverage ? `${coverageLabel} (${cov} per ${purchaseLabel})` : "coverage unit",
              !hasCoverage && basis !== "coverage_unit", `recipe-material-basis-coverage-${entry.id}`)}
            {coverageError && (
              <span className="text-note-red inline-flex items-center gap-1" style={{ fontWeight: 600 }}
                data-testid={`recipe-material-coverage-error-${entry.id}`}>
                <AlertTriangle size={11} /> Coverage missing on this material — entry fails
              </span>
            )}
          </div>
          {entry.stock_length_applied ? (
            <span className="text-ink-4">Stock length {entry.stock_length_applied} {purchaseLabel}</span>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export function MaterialsSection({ entries, onChange, materials, lineWastageOverride = null, flat = null, onFlatChange = null, refRate = null, initialBuildOut = false }) {
  // LT-18c §2 — a picked material carries its unit KEY (`m2`); the recipe
  // line's unit Select is keyed by LABEL (`m²`). Translate on pick so the
  // Select isn't left blank and the PDFs never see a slug.
  const { labelFor: unitLabelFor, units } = useMaterialUnits();
  const categories = useMaterialCategories();
  const update = (id, patch) => onChange(entries.map((e) => e.id === id ? { ...e, ...patch } : e));
  const remove = (id) => onChange(entries.filter((e) => e.id !== id));
  const add = () => onChange([...entries, {
    id: newId(), description: "", qty_per_unit: null, unit: "", rate: null, material_id: null,
    wastage_pct: null, coverage_basis: "purchase_unit"
  }]);
  // LT-36 — Flat Material Rate: one £/unit figure instead of itemised lines.
  const flatOn = !!(flat && flat.enabled);
  const itemisedTotal = materialsRatePerUnit(entries, lineWastageOverride);
  const total = flatOn ? f(flat.rate) : itemisedTotal;
  const toggleFlat = (on) => {
    if (!onFlatChange) return;
    if (on) onFlatChange({ enabled: true, rate: flat?.rate ?? (refRate != null ? refRate : null), note: flat?.note ?? "" });
    else onFlatChange({ ...(flat || {}), enabled: false });
  };
  // LT-36 build-out — itemise UNDER the flat rate while it still prices the
  // item; "Switch to itemised" is always the user's explicit call (b).
  const [buildOut, setBuildOut] = useState(!!initialBuildOut);
  useEffect(() => { if (initialBuildOut) setBuildOut(true); }, [initialBuildOut]);
  const startBuildOut = () => { setBuildOut(true); if (entries.length === 0) add(); };
  const switchToItemised = () => { onFlatChange({ ...flat, enabled: false }); setBuildOut(false); };
  const flatToggle = onFlatChange ? (
    <label className="ml-3 inline-flex items-center gap-1.5 cursor-pointer text-ink-2" style={{ fontSize: 11.5, fontWeight: 500 }} title="Price materials as one £ per unit figure — no itemised materials behind it (historic line items). Build the itemised recipe later.">
      <input type="checkbox" checked={flatOn} onChange={(e) => toggleFlat(e.target.checked)} data-testid="recipe-materials-flat-toggle" />
      Flat Material Rate
    </label>
  ) : null;
  // LT-20 — on pick, seed the *_applied fields from the picked material so
  // placeholders/basis are immediate; the server re-resolves on save/insert.
  const appliedFrom = (o) => {
    const unitRow = (units || []).find((u) => u.key === o.unit);
    return {
      material_wastage_pct_applied: o.default_wastage_pct ?? null,
      coverage_per_unit_applied: o.coverage_per_unit ?? null,
      coverage_unit_applied: o.coverage_unit ?? null,
      is_divisible_applied: unitRow ? !!unitRow.is_divisible : null,
      stock_length_applied: o.stock_length ?? null,
      unit_key_applied: o.unit || null,
    };
  };

  const itemisedTable = entries.length === 0 ? (
    <p className="text-ink-3 px-1 py-2" style={{ fontSize: 11.5 }}>
      No material entries yet. Add a material with per-unit quantity and rate.
    </p>
  ) : (
    <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
      <thead>
        <tr>
          <th className="col-head text-left" style={{ width: "44%", padding: "4px 6px" }}>Description</th>
          <ColHead>Qty / unit</ColHead>
          <ColHead>Unit</ColHead>
          <ColHead>Rate</ColHead>
          <ColHead>Per-unit cost</ColHead>
          <th style={{ width: 30 }} />
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => {
          const cost = entryCostPerUnit(e, lineWastageOverride);
          return (
          <React.Fragment key={e.id}>
          <tr className="border-t border-line-soft">
            <td className="px-1.5 py-1.5">
              <div className="flex items-center">
                {e.description && !e.material_id && (
                  <UnlinkedMarker kind="material" testid={`recipe-material-unlinked-${e.id}`} />
                )}
                <div className="flex-1">
                  <MaterialAutocomplete
                    value={e.description}
                    options={materials || []}
                    categories={categories}
                    placeholder="Material description (C24…)"
                    onChange={(v) => update(e.id, { description: v })}
                    onSelect={(o) => update(e.id, {
                      description: o.name, material_id: o.id, unit: unitLabelFor(o.unit),
                      rate: o.current_price ?? null,
                      ...appliedFrom(o)
                    })}
                  />
                </div>
              </div>
            </td>
            <td className="px-1 py-1.5" style={{ width: 88 }}>
              <NumberInput value={e.qty_per_unit} step="0.01" onChange={(v) => update(e.id, { qty_per_unit: v })} />
            </td>
            <td className="px-1 py-1.5" style={{ width: 100 }}>
              <Select
                value={e.unit || ""}
                onChange={(v) => update(e.id, { unit: v })}
                options={UNIT_OPTIONS}
                placeholder="—"
              />
            </td>
            <td className="px-1 py-1.5" style={{ width: 100 }}>
              <NumberInput value={e.rate} step="0.01" onChange={(v) => update(e.id, { rate: v })} prefix="£" />
            </td>
            <td className={["px-2 py-1.5 text-right font-mono tnum", cost == null ? "text-note-red" : "text-ink"].join(" ")}
                style={{ fontSize: 12.5, fontWeight: 500, width: 110 }}
                data-testid={`recipe-material-cost-${e.id}`}>
              {cost == null ? "fails" : gbp(cost)}
            </td>
            <td className="px-1 py-1.5">
              <button type="button" onClick={() => remove(e.id)} className="text-ink-4 hover:text-note-red"><X size={13} /></button>
            </td>
          </tr>
          <MaterialQuantityRow
            entry={e}
            onPatch={(patch) => update(e.id, patch)}
            lineWastageOverride={lineWastageOverride}
            unitLabelFor={unitLabelFor}
          />
          </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  if (flatOn) {
    const flatRate = f(flat.rate);
    const diff = itemisedTotal - flatRate;
    const reached = flatRate > 0 && itemisedTotal >= flatRate - 0.005;
    const canSwitch = entries.some((e) => (entryCostPerUnit(e, lineWastageOverride) || 0) > 0);
    return (
      <SectionShell title="Materials" icon={Package} accent="#F8F1DF" total={total} onAdd={buildOut ? add : null} addLabel="Add material" headerExtra={flatToggle}>
        <div className="px-1 py-2" data-testid="recipe-materials-flat">
          <div className="grid gap-3" style={{ gridTemplateColumns: "180px 1fr" }}>
            <div>
              <div className="col-head mb-1" style={{ fontSize: 9.5 }}>Material rate per unit</div>
              <NumberInput value={flat.rate} onChange={(v) => onFlatChange({ ...flat, rate: v })} prefix="£" suffix="/unit" placeholder="0.00" data-testid="recipe-materials-flat-rate" />
            </div>
            <div>
              <div className="col-head mb-1" style={{ fontSize: 9.5 }}>What it covers (optional)</div>
              <input type="text" value={flat.note || ""} onChange={(e) => onFlatChange({ ...flat, note: e.target.value })}
                placeholder="e.g. boards, fixings, adhesive — from historic rates"
                className="w-full h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                style={{ fontSize: 12.5 }} data-testid="recipe-materials-flat-note" />
            </div>
          </div>
          <p className="text-ink-3 mt-2" style={{ fontSize: 11 }}>
            One figure, nothing itemised — the order list will show this line as "flat material rate, nothing to order".
            {refRate != null && <> Imported reference material rate: <span className="font-mono tnum">{gbp(refRate)}</span>/unit
              {f(flat.rate) !== f(refRate) && <button type="button" onClick={() => onFlatChange({ ...flat, rate: refRate })} className="ml-1 text-accent hover:underline" data-testid="recipe-materials-flat-use-ref">use it</button>}.</>}
            {!buildOut && entries.length > 0 && <> {entries.length} itemised line{entries.length === 1 ? "" : "s"} kept but not counted while the flat rate is on.</>}
            {!buildOut && (
              <button type="button" onClick={startBuildOut} className="ml-1.5 text-accent hover:underline" style={{ fontWeight: 500 }} data-testid="recipe-materials-build-out">
                Build out recipe →
              </button>
            )}
          </p>
        </div>
        {buildOut && (
          <div className="mt-1 rounded-md border border-line bg-bg" data-testid="recipe-materials-buildout">
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-line-soft flex-wrap" data-testid="recipe-materials-buildout-tracker">
              <div className="min-w-0">
                <div className="text-ink" style={{ fontSize: 12 }}>
                  Itemised so far <span className="font-mono tnum" style={{ fontWeight: 700 }} data-testid="recipe-materials-buildout-itemised">{gbp(itemisedTotal)}</span>
                  {" "}vs flat <span className="font-mono tnum" data-testid="recipe-materials-buildout-flat">{gbp(flatRate)}</span>
                  {" — "}
                  <span className={reached ? "text-accent-deep" : "text-ink-3"} data-testid="recipe-materials-buildout-diff">
                    {Math.abs(diff) < 0.005 ? "matches the flat figure" : diff > 0 ? `${gbp(diff)} over` : `${gbp(-diff)} to go`}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-surface-2 overflow-hidden" style={{ width: 260 }}>
                  <div className={["h-full rounded-full transition-[width]", reached ? "bg-accent" : "bg-warning"].join(" ")}
                    style={{ width: `${flatRate > 0 ? Math.min(100, (itemisedTotal / flatRate) * 100) : (itemisedTotal > 0 ? 100 : 0)}%` }} />
                </div>
                <div className="text-ink-4 mt-1" style={{ fontSize: 10.5 }}>
                  The flat rate still prices this item until you switch. Rates evolving upwards is expected — the change is shown, not blocked.
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={() => setBuildOut(false)} className="text-ink-3 hover:text-ink" style={{ fontSize: 11.5 }} data-testid="recipe-materials-build-out-hide">Hide</button>
                <button type="button" onClick={switchToItemised} disabled={!canSwitch}
                  className={[
                    "inline-flex items-center gap-1.5 h-8 px-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                    reached ? "bg-accent text-white hover:bg-accent-deep" : "border border-line bg-surface text-ink-2 hover:border-accent hover:text-accent-deep",
                  ].join(" ")}
                  style={{ fontSize: 12, fontWeight: 500 }}
                  title={canSwitch ? `Turn the flat rate off — materials become ${gbp(itemisedTotal)}/unit from the itemised lines (flat figure is remembered)` : "Add at least one priced material line first"}
                  data-testid="recipe-materials-switch-itemised">
                  Switch to itemised
                </button>
              </div>
            </div>
            <div className="px-2 pt-1 pb-2">{itemisedTable}</div>
          </div>
        )}
      </SectionShell>
    );
  }

  return (
    <SectionShell title="Materials" icon={Package} accent="#F8F1DF" total={total} onAdd={add} addLabel="Add material" headerExtra={flatToggle}>
      {itemisedTable}
    </SectionShell>
  );
}

export function SimpleCostSection({ title, icon, accent, entries, onChange, placeholder, addLabel }) {
  const update = (id, patch) => onChange(entries.map((e) => e.id === id ? { ...e, ...patch } : e));
  const remove = (id) => onChange(entries.filter((e) => e.id !== id));
  const add = () => onChange([...entries, { id: newId(), description: "", cost_per_unit: null }]);
  const total = entries.reduce((s, e) => s + f(e.cost_per_unit), 0);

  return (
    <SectionShell title={title} icon={icon} accent={accent} total={total} onAdd={add} addLabel={addLabel}>
      {entries.length === 0 ? (
        <p className="text-ink-3 px-1 py-2" style={{ fontSize: 11.5 }}>{placeholder}</p>
      ) : (
        <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="col-head text-left" style={{ padding: "4px 6px" }}>Description</th>
              <ColHead>Per-unit cost</ColHead>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-line-soft">
                <td className="px-1.5 py-1.5">
                  <input
                    type="text"
                    value={e.description || ""}
                    spellCheck="true"
                    onChange={(ev) => update(e.id, { description: ev.target.value })}
                    onFocus={(ev) => requestAnimationFrame(() => { try { ev.target.select(); } catch (_) {} })}
                    onKeyDown={blockEnterSubmit}
                    placeholder="Describe…"
                    className="w-full bg-transparent border-0 outline-none rounded px-1.5 py-1 focus:bg-bg focus:ring-1 focus:ring-accent/40 text-ink"
                    style={{ fontSize: 12.5 }}
                  />
                </td>
                <td className="px-1 py-1.5" style={{ width: 110 }}>
                  <NumberInput value={e.cost_per_unit} step="0.01" onChange={(v) => update(e.id, { cost_per_unit: v })} prefix="£" />
                </td>
                <td className="px-1 py-1.5">
                  <button type="button" onClick={() => remove(e.id)} className="text-ink-4 hover:text-note-red"><X size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionShell>
  );
}

/* ============= Recipe Summary panel ============= */
export function RecipeSummary({ totals, unit }) {
  const labour    = totals.recipe_labour_total;
  const materials = totals.recipe_materials_total;
  const plant     = totals.recipe_plant_total;
  const subby     = totals.recipe_subby_total;
  const flat      = !!totals.flat_material;
  // Per spec (Library Refresh): Total per unit = sum of Labour + Material + Plant
  const total = (labour || 0) + (materials || 0) + (plant || 0);

  const seg = (label, value, bold, testId) => (
    <div className="flex items-baseline justify-between py-1.5 px-3" data-testid={testId}>
      <span className={["text-ink-2", bold && "font-medium"].filter(Boolean).join(" ")} style={{ fontSize: 12 }}>{label}</span>
      <span className={["font-mono tnum", bold ? "text-ink" : "text-ink-2"].join(" ")}
            style={{ fontSize: bold ? 14 : 12.5, fontWeight: bold ? 700 : 500 }}>{gbp(value)}</span>
    </div>
  );

  return (
    <div className="rounded-lg border border-line bg-surface-2/50 overflow-hidden" data-testid="recipe-summary">
      <div className="px-3 py-2 border-b border-line bg-surface-2">
        <span className="col-head" style={{ fontWeight: 700 }}>Recipe summary {unit ? `(per ${unit})` : ""}</span>
      </div>
      {seg("Labour",    labour)}
      {seg(flat ? "Materials (flat rate)" : "Materials", materials, false, "recipe-summary-materials")}
      {seg("Plant",     plant)}
      {subby > 0 && seg("Subby",     subby)}
      <div className="border-t border-line">
        {seg("Total per unit", total, true)}
      </div>
      {!totals.has_recipe && (
        <div className="px-3 py-2 text-ink-3 border-t border-line-soft" style={{ fontSize: 11 }}>
          No recipe entries yet. Add labour, materials or plant lines below.
        </div>
      )}
    </div>
  );
}

/* ============= Compound RecipeBuilder ============= */
/**
 * High-level component that composes the four sections + summary.
 * Used by the Library Edit page and the per-line Recipe drawer.
 *
 * Props:
 *  arrays    — { recipe_labour, recipe_materials, recipe_plant, recipe_subby }
 *  onChange  — called with `{ ...arrays, [key]: nextValue }` on every edit
 *  totals    — from `computeRecipeTotals(arrays)` for the summary panel
 *  unit      — recipe yield unit (for "per X" suffix in summary)
 *  labourRates / materials — master-list options for autocomplete
 *  showSubby — render the Subby section even when empty (default: only when populated)
 */
export function RecipeBuilder({
  arrays, onChange, totals, unit,
  labourRates = [], materials = [], showSubby = false,
  className = "", lineWastageOverride = null
}) {
  const setKey = (k) => (v) => onChange({ ...arrays, [k]: v });
  // LT-36 — the snapshot carries `flat_material: {rate, note}` when the library
  // item priced materials as one figure; the drawer edits it in the same shape.
  const flat = arrays.flat_material ? { enabled: true, rate: arrays.flat_material.rate, note: arrays.flat_material.note || "" } : null;
  const onFlatChange = (v) => onChange({ ...arrays, flat_material: v && v.enabled ? { rate: v.rate, note: v.note || null } : null });
  return (
    <div className={className}>
      <LabourSection
        entries={arrays.recipe_labour || []}
        onChange={setKey("recipe_labour")}
        labourRates={labourRates}
      />
      <MaterialsSection
        entries={arrays.recipe_materials || []}
        onChange={setKey("recipe_materials")}
        materials={materials}
        lineWastageOverride={lineWastageOverride}
        flat={flat}
        onFlatChange={onFlatChange}
      />
      <SimpleCostSection
        title="Plant" icon={Wrench} accent="#F1ECF6"
        entries={arrays.recipe_plant || []} onChange={setKey("recipe_plant")}
        placeholder="No plant entries yet (e.g. skip hire share, scaffold contribution)."
        addLabel="Add plant cost"
      />
      {(showSubby || (arrays.recipe_subby && arrays.recipe_subby.length > 0)) && (
        <SimpleCostSection
          title="Subby / PS" icon={Sparkles} accent="#EBE9F4"
          entries={arrays.recipe_subby || []} onChange={setKey("recipe_subby")}
          placeholder="No subby costs."
          addLabel="Add subby cost"
        />
      )}
      {totals && (
        <div className="mt-3">
          <RecipeSummary totals={totals} unit={unit} />
        </div>
      )}
    </div>
  );
}
